import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Loader2, User, Mail, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import frequentContactsService, { FrequentContact } from '../services/frequentContacts.service';
import { ContactDataTable } from './ContactDataTable';
import { useTranslation } from 'react-i18next';

const typeLabels = {
  supplier: 'Lieferant',
  warehouse: 'Lager',
  shipping: 'Versand',
  vendor: 'Anbieter',
  partner: 'Partner',
  other: 'Sonstiges'
};

const FrequentContactsSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<FrequentContact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'other' as 'supplier' | 'warehouse' | 'shipping' | 'vendor' | 'partner' | 'other'
  });

  // Fetch contacts
  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['frequentContacts'],
    queryFn: () => frequentContactsService.listContacts(),
  });
  const contacts = contactsData?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: frequentContactsService.createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['frequentContacts'] });
      handleCloseDialog();
      toast.success(t('common:x3.contactCreated', 'Kontakt erstellt'));
    },
    onError: () => {
      toast.error(t('common:x3.contactCreateFailed', 'Fehler beim Erstellen des Kontakts'));
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; email: string; type?: string } }) =>
      frequentContactsService.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['frequentContacts'] });
      handleCloseDialog();
      toast.success(t('common:x3.contactUpdated', 'Kontakt aktualisiert'));
    },
    onError: () => {
      toast.error(t('common:x3.contactUpdateFailed', 'Fehler beim Aktualisieren des Kontakts'));
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: frequentContactsService.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['frequentContacts'] });
      toast.success(t('common:x3.contactDeleted', 'Kontakt gelöscht'));
    },
    onError: () => {
      toast.error(t('common:x3.contactDeleteFailed', 'Fehler beim Löschen des Kontakts'));
    }
  });

  const handleSave = () => {
    if (!formData.name || !formData.email) return;

    if (editingContact) {
      updateMutation.mutate({
        id: editingContact.id,
        data: {
          name: formData.name,
          email: formData.email,
          type: formData.type
        },
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        email: formData.email,
        type: formData.type
      });
    }
  };

  const handleDelete = (contact: FrequentContact) => {
    if (confirm(`Sind Sie sicher, dass Sie ${contact.name} löschen möchten?`)) {
      deleteMutation.mutate(contact.id);
    }
  };

  const handleEdit = (contact: FrequentContact) => {
    setEditingContact(contact);
    setFormData({ 
      name: contact.name, 
      email: contact.email, 
      type: (contact.type as any) || 'other' 
    });
    setIsCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingContact(null);
    setFormData({ name: '', email: '', type: 'other' });
  };

  return (
    <div className="space-y-6">
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-900">{t('common:x3.frequentContacts', 'Häufige Kontakte')}</CardTitle>
              <span className="text-xs text-slate-400 font-normal ml-2">({contacts.length} Kontakte)</span>
            </div>
            <CardDescription className="text-sm text-slate-500">
              Verwalten Sie häufig verwendete Kontakte für schnellen Zugriff bei der Ticket-Erstellung.
            </CardDescription>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setEditingContact(null);
                  setFormData({ name: '', email: '', type: 'other' });
                  setIsCreateDialogOpen(true);
                }}
                className="h-9 gap-2 bg-slate-900 text-white hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                {t('common:x3.newContact', 'Neuer Kontakt')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
              <div className="flex flex-1 overflow-hidden">
                {/* Sidebar */}
                <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {editingContact ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {t('common:x3.addSuppliersPartners', 'Fügen Sie Lieferanten, Partner oder andere wichtige Kontakte hinzu.')}
                    </p>
                  </div>
                  
                  <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('common:x3.types', 'Typen')}</span>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <span>{t('agent:dept.supplier', 'Lieferant')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span>{t('agent:dept.partner', 'Partner')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                        <span>{t('agent:dept.shipping', 'Versand')}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 bg-white min-w-0">
                  <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                    <DialogTitle className="text-base font-semibold text-slate-900">
                      {t('common:x3.contactDetails', 'Kontaktdetails')}
                    </DialogTitle>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6 max-w-lg">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs font-medium text-slate-700">{t('common:x1.nameReq', 'Name *')}</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="name"
                            placeholder={t('common:x3.egMaxMustermannGmbh', 'z.B. Max Mustermann GmbH')}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="h-9 pl-9 border-slate-200 focus-visible:ring-slate-200"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-medium text-slate-700">E-Mail *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="email"
                            type="email"
                            placeholder="kontakt@firma.de"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="h-9 pl-9 border-slate-200 focus-visible:ring-slate-200"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="type" className="text-xs font-medium text-slate-700">{t('common:field.category', 'Typ')}</Label>
                        <Select 
                          value={formData.type} 
                          onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                        >
                          <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200">
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-slate-400" />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="supplier">{t('agent:dept.supplier', 'Lieferant')}</SelectItem>
                            <SelectItem value="warehouse">{t('agent:dept.warehouse', 'Lager')}</SelectItem>
                            <SelectItem value="shipping">{t('agent:dept.shipping', 'Versand')}</SelectItem>
                            <SelectItem value="vendor">{t('agent:dept.vendor', 'Anbieter')}</SelectItem>
                            <SelectItem value="partner">{t('agent:dept.partner', 'Partner')}</SelectItem>
                            <SelectItem value="other">{t('agent:dept.other', 'Sonstiges')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      onClick={handleCloseDialog}
                      className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                    >
                      {t('common:action.cancel', 'Abbrechen')}
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={!formData.name || !formData.email || createMutation.isPending || updateMutation.isPending}
                      className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {createMutation.isPending || updateMutation.isPending ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                          {t('common:state.savingShort', 'Speichert...')}
                        </>
                      ) : (
                        <>
                          <Save className="h-3 w-3 mr-2" />
                          {t('common:action.save', 'Speichern')}
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <ContactDataTable 
            contacts={contacts}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isDeletePending={deleteMutation.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default FrequentContactsSection;
